/**
 * API URL filtering — include/exclude patterns for API call tracking.
 *
 * Supports glob strings and RegExp objects. Exclude takes precedence over include.
 */

/**
 * Determines whether an API call URL should be tracked.
 *
 * - No filters → track all
 * - Include set → URL must match at least one include pattern
 * - Exclude set → URL must not match any exclude pattern
 * - Both set → exclude takes precedence
 */
export function shouldTrackApiUrl(
  url: string,
  include: readonly (string | RegExp)[],
  exclude: readonly (string | RegExp)[],
): boolean {
  // Check exclude first (takes precedence)
  if (exclude.length > 0) {
    for (const pattern of exclude) {
      if (matchesPattern(url, pattern)) return false;
    }
  }

  // Check include
  if (include.length > 0) {
    for (const pattern of include) {
      if (matchesPattern(url, pattern)) return true;
    }
    return false; // Include set but no match → don't track
  }

  return true; // No filters → track all
}

/**
 * Test a URL against a single pattern (glob string or RegExp).
 */
function matchesPattern(url: string, pattern: string | RegExp): boolean {
  try {
    if (pattern instanceof RegExp) {
      return pattern.test(url);
    }
    // Convert glob to regex
    const regex = globToRegex(pattern);
    return regex.test(url);
  } catch {
    // Invalid pattern — skip with warning
    console.warn(`[testrelic] Invalid URL filter pattern: ${String(pattern)}`);
    return false;
  }
}

/**
 * Convert a glob pattern to a RegExp.
 * Handles `**` (match anything including /) and `*` (match anything except /).
 */
function globToRegex(glob: string): RegExp {
  let result = '';
  let i = 0;
  while (i < glob.length) {
    const char = glob[i];
    if (char === '*' && glob[i + 1] === '*') {
      result += '.*';
      i += 2;
      // Skip optional trailing /
      if (glob[i] === '/') i++;
    } else if (char === '*') {
      result += '[^/]*';
      i++;
    } else if (char === '?') {
      result += '[^/]';
      i++;
    } else if ('.+^${}()|[]\\'.includes(char)) {
      result += '\\' + char;
      i++;
    } else {
      result += char;
      i++;
    }
  }
  return new RegExp(result);
}
